import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Input, 
  Button, 
  message,
  Typography,
  Avatar,
  Tag
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import PageLayout from '../../../components/layout/PageLayout';
import videoService from '../../../services/videoService';
import { useUser } from '../../../context/UserContext';
import moment from 'moment';

const { Title, Text } = Typography;
const { Search } = Input;

const ShowList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [shows, setShows] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} shows`
  });
  
  // Filters
  const [filters, setFilters] = useState({
    search: ''
  });

  const getShows = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      
      const requestParams = {
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        searchText: filters.search,
        ...params
      };
      
      console.log('Fetching shows with params:', requestParams);
      const response = await videoService.getAllVideoShow(requestParams);
      console.log('Shows API Response:', response);
      
      if (response.data && response.data.status) {
        const showsData = response.data.data?.data || [];
        const paginationData = response.data.data?.pagination || {};
        
        console.log('Shows data:', showsData);
        console.log('Pagination data:', paginationData);
        
        // Ensure showsData is an array
        if (Array.isArray(showsData)) {
          setShows(showsData);
          setPagination(prev => ({
            ...prev,
            total: paginationData.total || showsData.length,
            current: paginationData.currentPage || prev.current
          }));
        } else {
          console.error('Shows data is not an array:', showsData);
          setShows([]);
        }
      } else {
        message.error('Failed to fetch video shows');
        setShows([]);
      }
    } catch (error) {
      console.error('Error fetching shows:', error);
      message.error('Failed to fetch video shows');
      setShows([]);
    } finally {
      setLoading(false);
    }
  }, [filters.search, pagination.pageSize, pagination.current]);

  // Load shows data
  useEffect(() => {
    getShows();
  }, [getShows]);

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getShows({
      searchText: value,
      currentPage: 1
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;
    
    setPagination(prev => ({
      ...prev,
      current,
      pageSize
    }));
    
    getShows({
      currentPage: current,
      perPage: pageSize,
      searchText: filters.search
    });
  };

  // Handle add show
  const handleAddShow = () => {
    navigate('/Video/Show/addVideoShow');
  };

  // Handle edit show
  const handleEditShow = (videoShowId) => {
    navigate(`/Video/Show/editVideoShow/${videoShowId}`);
  };

  // Format date from timestamp
  const getDateFromTs = (timestamp) => {
    if (timestamp) {
      return moment.unix(timestamp).format('YYYY-MM-DD HH:mm:ss');
    }
    return 'No Date';
  };

  // Table columns
  const columns = [
    {
      title: 'Show Title',
      dataIndex: 'video_show_title',
      key: 'video_show_title',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.video_show_title.localeCompare(b.video_show_title),
      render: (text) => (
        <Text strong style={{ fontSize: '14px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Run Time',
      dataIndex: 'vs_runtime',
      key: 'vs_runtime',
      width: 120,
      render: (text) => (
        <Text style={{ fontSize: '14px' }}>
          {text ? `${text} min` : 'N/A'}
        </Text>
      )
    },
    {
      title: 'Release Date',
      dataIndex: 'vs_release_date_ts',
      key: 'vs_release_date_ts',
      width: 180,
      render: (text) => (
        <Text style={{ fontSize: '13px' }}>
          {getDateFromTs(text)}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'video_show_is_active',
      key: 'video_show_is_active',
      width: 120,
      render: (status) => (
        <Tag color={status === 'Y' ? 'green' : 'red'}>
          {status === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditShow(record.video_show_id)}
          size="small"
        >
          Edit
        </Button>
      )
    }
  ];

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Manage Video Shows</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Video Show List"
      desc="Manage video shows and their details"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <Title level={4} style={{ margin: 0 }}>
              Video Shows ({pagination.total})
            </Title>
            
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddShow}
              size="large"
            >
              Add Movie
            </Button>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Search
              placeholder="Search shows..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '50%', minWidth: '200px' }}
              enterButton={<SearchOutlined />}
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={shows}
          rowKey="video_show_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          size="middle"
          locale={{ 
            emptyText: 'No video shows found',
            filterConfirm: 'OK',
            filterReset: 'Reset',
            filterEmptyText: 'No filters'
          }}
        />
      </Card>
    </PageLayout>
  );
};

export default ShowList;
